import { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Fab } from '@components/Fab.tsx'
import { SearchInput } from '@components/SearchInput.tsx'
import { useFavicon } from '@hooks/useFavicon.ts'
import { useResource } from '@hooks/useResource.ts'
import { useWakeLock } from '@hooks/useWakeLock.ts'
import { bestScore, MATCH_THRESHOLD } from '@utils/search.ts'
import { api, ApiError } from '@api'
import { useRouter } from '@router'
import { flatIngredients, type Recipe, type RecipeId } from '@hewey/shared'
import { AddRecipeDialog } from './AddRecipeDialog.tsx'
import { AllRecipesPage } from './AllRecipesPage.tsx'
import { ExploreCollections } from './ExploreCollections.tsx'
import { PendingRecipeCard, RecipeCard } from './RecipeCard.tsx'
import { RecipeDetail } from './RecipeDetail.tsx'
import { RecipeForm } from './RecipeForm.tsx'
import { RecipeFilterBar } from './RecipeFilterBar.tsx'
import {
	DEFAULT_RECIPE_FILTER,
	isDefaultRecipeFilter,
	matchesFilter,
	type RecipeFilter,
	useRecipeFilter,
} from './filter.ts'
import styles from './Recipes.module.css'

// Poll while any recipe still has taggingStatus 'pending' AND is fresh
// enough that the server might reasonably still be working on it. After
// 90s the tag pill is hidden either way (best-effort tagging — see
// notes/recipes-phase-kickoff.md) so there's nothing to gain by polling
// further.
const POLL_INTERVAL_MS = 3_000
const TAGGING_FRESH_MS = 90_000

const recipeIsTaggingFresh = (r: Recipe): boolean =>
	Date.now() - new Date(r.createdAt).getTime() < TAGGING_FRESH_MS

const byCreatedDesc = (a: Recipe, b: Recipe): number =>
	b.createdAt.localeCompare(a.createdAt)

type PendingIngest = { tempId: string; url: string }

type View =
	| { kind: 'list' }
	| { kind: 'all' }
	| { kind: 'new' }
	| { kind: 'detail'; id: RecipeId }

export const RecipesApp = () => {
	useFavicon('recipes')
	const { path, navigate } = useRouter()
	// Poll so a recipe added on another family member's phone shows up here.
	const recipes = useResource(['recipes'], () => api.recipe.list(), {
		pollIntervalMs: 10_000,
	})

	const [addOpen, setAddOpen] = useState(false)
	const [pending, setPending] = useState<PendingIngest[]>([])
	const [error, setError] = useState<string | null>(null)
	const [wakeLock, setWakeLock] = useState(false)
	const [query, setQuery] = useState('')
	const [filter, setFilter] = useRecipeFilter()

	// Held at RecipesApp level so the lock survives navigating between the
	// list and any recipe detail without dropping.
	useWakeLock(wakeLock)

	const list = recipes.data?.recipes ?? []

	// Explore shows its suggested collections until the user narrows — a search
	// query or any active filter flips it into the results grid.
	const trimmedQuery = query.trim()
	const narrowing = trimmedQuery !== '' || !isDefaultRecipeFilter(filter)

	const results = useMemo(() => {
		const matched = list.filter((r) => matchesFilter(r, filter))
		if (trimmedQuery === '') return [...matched].sort(byCreatedDesc)
		// With a query, rank by forgiving title/ingredient match, drop the rest.
		return matched
			.map((r) => ({
				r,
				score: bestScore(trimmedQuery, [
					r.title,
					flatIngredients(r).join(' '),
				]),
			}))
			.filter((x) => x.score >= MATCH_THRESHOLD)
			.sort((a, b) => b.score - a.score)
			.map((x) => x.r)
	}, [list, filter, trimmedQuery])

	const clearNarrowing = () => {
		setFilter(DEFAULT_RECIPE_FILTER)
		setQuery('')
	}

	// "See all" on a collection row: replace the filter with the row's patch and
	// clear any query, dropping the user into the matching results grid.
	const onSeeAll = (patch: Partial<RecipeFilter>) => {
		setFilter({ ...DEFAULT_RECIPE_FILTER, ...patch })
		setQuery('')
	}

	// Route within /recipes — `/recipes/all` is the full A–Z list; `/recipes/new`
	// is the manual create form (matched before the detail regex, which would
	// otherwise capture "new" as an id); `/recipes/<id>` is detail; anything else
	// (including a trailing slash) is the Explore landing.
	const view = useMemo<View>(() => {
		if (path === '/recipes/all') return { kind: 'all' }
		if (path === '/recipes/new') return { kind: 'new' }
		const m = /^\/recipes\/([^/]+)$/.exec(path)
		if (m) return { kind: 'detail', id: m[1] as RecipeId }
		return { kind: 'list' }
	}, [path])

	const freshPending = useMemo(
		() =>
			list.some(
				(r) => r.taggingStatus === 'pending' && recipeIsTaggingFresh(r),
			),
		[list],
	)

	useEffect(() => {
		if (!freshPending) return
		const id = setInterval(() => {
			void recipes.refetch()
		}, POLL_INTERVAL_MS)
		return () => clearInterval(id)
	}, [freshPending, recipes])

	if (view.kind === 'all') {
		return <AllRecipesPage recipes={list} loading={recipes.loading} />
	}

	if (view.kind === 'new') {
		return (
			<RecipeForm
				onCreated={(recipe) =>
					// Upsert: create prepends, then the follow-up photo upload
					// calls again with the same id — replace rather than dupe.
					recipes.set((prev) => ({
						recipes: [
							recipe,
							...(prev?.recipes ?? []).filter(
								(r) => r.id !== recipe.id,
							),
						],
					}))
				}
			/>
		)
	}

	if (view.kind === 'detail') {
		const detailId = view.id
		const recipe = list.find((r) => r.id === detailId) ?? null
		return (
			<RecipeDetail
				recipeId={detailId}
				recipe={recipe}
				loading={recipes.loading}
				wakeLock={wakeLock}
				onToggleWakeLock={() => setWakeLock((v) => !v)}
				onPatched={(updated) =>
					recipes.set((prev) =>
						prev
							? {
									recipes: prev.recipes.map((r) =>
										r.id === updated.id ? updated : r,
									),
								}
							: prev,
					)
				}
				onDeleted={(id) => {
					recipes.set((prev) =>
						prev
							? {
									recipes: prev.recipes.filter(
										(r) => r.id !== id,
									),
								}
							: prev,
					)
					navigate('/recipes')
				}}
			/>
		)
	}

	// Optimistic ingest: drop a pending card in immediately and scrape in
	// the background so the dialog can close right away. The pending card
	// swaps for the real recipe (or vanishes, with an error banner) once
	// the create resolves.
	const ingest = (url: string) => {
		const tempId = `pending-${crypto.randomUUID()}`
		setPending((p) => [{ tempId, url }, ...p])
		setError(null)
		void (async () => {
			try {
				const res = await api.recipe.create({ kind: 'url', url })
				setPending((p) => p.filter((x) => x.tempId !== tempId))
				recipes.set((prev) => ({
					recipes: [res.recipe, ...(prev?.recipes ?? [])],
				}))
			} catch (e) {
				setPending((p) => p.filter((x) => x.tempId !== tempId))
				setError(
					e instanceof ApiError
						? e.message
						: 'Something went wrong adding that recipe.',
				)
			}
		})()
	}

	return (
		<main className={styles.screen}>
			{error ? <p className={styles.error}>{error}</p> : null}

			{list.length > 0 ? (
				<>
					<SearchInput
						value={query}
						onChange={setQuery}
						placeholder="Search recipes"
					/>
					<RecipeFilterBar filter={filter} setFilter={setFilter} />
				</>
			) : null}

			{pending.length > 0 ? (
				<ul className={styles.grid}>
					{pending.map((p) => (
						<li key={p.tempId}>
							<PendingRecipeCard url={p.url} />
						</li>
					))}
				</ul>
			) : null}

			{recipes.error ? (
				<p className={styles.empty}>
					Couldn't load recipes.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={() => void recipes.refetch()}
					>
						Retry
					</button>
				</p>
			) : recipes.loading && list.length === 0 && pending.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : list.length === 0 && pending.length === 0 ? (
				<p className={styles.empty}>
					Nothing yet. Tap + Add to add a recipe.
				</p>
			) : list.length === 0 ? null : !narrowing ? (
				<ExploreCollections recipes={list} onSeeAll={onSeeAll} />
			) : results.length === 0 ? (
				<p className={styles.empty}>
					No recipes match.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={clearNarrowing}
					>
						Clear search and filters
					</button>
				</p>
			) : (
				<ul className={styles.grid}>
					{results.map((r) => (
						<li key={r.id}>
							<RecipeCard
								recipe={r}
								taggingFresh={recipeIsTaggingFresh(r)}
							/>
						</li>
					))}
				</ul>
			)}

			<Fab icon={Plus} label="Add" onClick={() => setAddOpen(true)} />

			<AddRecipeDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onSubmit={ingest}
			/>
		</main>
	)
}
